/*
 * lokator100 mobile swipe gallery v1.0.2
 * Requires the unmodified PhotoSwipe 5.4.4 ESM core and CSS in theme assets.
 * Additive bridge for Shopify Studio/Dawn modal-opener + ProductModal markup.
 * No changes to desktop, video openers, filters, product forms or native sliders.
 */
(() => {
  'use strict';
  if (window.__lokatorSwipeGalleryLoaded) return;
  window.__lokatorSwipeGalleryLoaded = true;
  window.__lokatorSwipeGalleryVersion = '1.0.2';

  const CONFIG_SELECTOR = 'script[type="application/json"][data-lkg-config]';
  const OPENER_SELECTOR = 'modal-opener.product__modal-opener--image[data-modal]';
  const modulePromises = new Map();
  const stylePromises = new Map();
  const configCache = new WeakMap();
  const bypass = new WeakSet();
  let opening = false;
  let active = null;

  const text = {
    en: {
      gallery: 'Product images', close: 'Close gallery', zoom: 'Zoom image',
      previous: 'Previous image', next: 'Next image', error: 'The image could not be loaded.'
    },
    de: {
      gallery: 'Produktbilder', close: 'Galerie schließen', zoom: 'Bild vergrößern',
      previous: 'Vorheriges Bild', next: 'Nächstes Bild', error: 'Das Bild konnte nicht geladen werden.'
    }
  };

  function getConfig(modalId) {
    const node = Array.from(document.querySelectorAll(CONFIG_SELECTOR))
      .find((candidate) => candidate.dataset.modalId === modalId);
    if (!node) return null;
    const raw = node.textContent;
    const cached = configCache.get(node);
    if (cached?.raw === raw) return cached.config;
    try {
      const config = JSON.parse(raw);
      if (config.modalId !== modalId || !Array.isArray(config.images)) return null;
      config.images = config.images.filter((image) =>
        image && typeof image.src === 'string' && image.id != null &&
        Number(image.width) > 0 && Number(image.height) > 0
      ).map((image) => ({ ...image, id: String(image.id) }));
      configCache.set(node, { raw, config });
      return config;
    } catch (error) {
      console.warn('[lokator gallery] Invalid configuration; native viewer retained.', error);
      return null;
    }
  }

  function isMobile(config) {
    const max = Number(config.maxWidth) || 989;
    return window.matchMedia(`(max-width: ${max}px)`).matches;
  }

  function requestFromEvent(event) {
    if (!(event.target instanceof Element)) return null;
    const button = event.target.closest('button[data-media-id]');
    if (!button || bypass.has(button) || button.closest('quick-add-modal')) return null;
    const opener = button.closest(OPENER_SELECTOR);
    if (!opener) return null;
    const selector = opener.getAttribute('data-modal') || '';
    if (!selector.startsWith('#ProductModal-')) return null;
    const config = getConfig(selector.slice(1));
    if (!config || !isMobile(config)) return null;
    const imageId = String(button.dataset.mediaId);
    if (!config.images.some((image) => image.id === imageId)) return null;
    return { config, opener, button, imageId };
  }

  function loadModule(url) {
    const absolute = new URL(url, document.baseURI).href;
    if (!modulePromises.has(absolute)) {
      const promise = import(absolute).then((module) => {
        if (typeof module.default !== 'function') throw new Error('Invalid PhotoSwipe module.');
        return module.default;
      }).catch((error) => {
        modulePromises.delete(absolute);
        throw error;
      });
      modulePromises.set(absolute, promise);
    }
    return modulePromises.get(absolute);
  }

  function loadStyles(url) {
    const absolute = new URL(url, document.baseURI).href;
    if (stylePromises.has(absolute)) return stylePromises.get(absolute);
    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = absolute;
      link.dataset.lkgVendor = 'true';
      let complete = false;
      const finish = (error) => {
        if (complete) return;
        complete = true;
        clearTimeout(timer);
        link.onload = link.onerror = null;
        if (error) { link.remove(); reject(error); }
        else resolve();
      };
      const timer = setTimeout(() => finish(new Error('PhotoSwipe CSS load timeout.')), 10000);
      link.onload = () => finish();
      link.onerror = () => finish(new Error('PhotoSwipe CSS could not be loaded.'));
      document.head.appendChild(link);
    }).catch((error) => { stylePromises.delete(absolute); throw error; });
    stylePromises.set(absolute, promise);
    return promise;
  }

  async function dependencies(config) {
    if (!config.moduleUrl || !config.styleUrl) throw new Error('Missing PhotoSwipe asset URLs.');
    let timer;
    try {
      const [PhotoSwipe] = await Promise.race([
        Promise.all([loadModule(config.moduleUrl), loadStyles(config.styleUrl)]),
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error('PhotoSwipe load timeout.')), 10000);
        })
      ]);
      return PhotoSwipe;
    } finally { clearTimeout(timer); }
  }

  function slidesFor(request) {
    const { config, imageId, opener } = request;
    const byId = new Map(config.images.map((image) => [image.id, image]));
    const gallery = opener.closest('media-gallery') || document.getElementById(config.galleryId);
    const ordered = [];
    const seen = new Set();
    let hasImageItems = false;
    if (gallery) {
      // Use rendered image order and omit variants actually hidden with display:none/hidden.
      // Do not omit off-screen slides merely because they are outside the scroll viewport.
      gallery.querySelectorAll('.product__media-list .product__media-item').forEach((item) => {
        const toggle = item.querySelector(`${OPENER_SELECTOR} button[data-media-id]`);
        const id = toggle && String(toggle.dataset.mediaId);
        if (!id || !byId.has(id)) return;
        hasImageItems = true;
        if (seen.has(id) || item.hidden || getComputedStyle(item).display === 'none') return;
        seen.add(id);
        ordered.push(byId.get(id));
      });
    }
    // Generic fallback when a customized gallery has no standard media-item wrappers.
    const selected = hasImageItems ? ordered : config.images;
    if (!selected.some((image) => image.id === imageId)) return null;
    return {
      index: selected.findIndex((image) => image.id === imageId),
      slides: selected.map((image) => {
        const slide = { ...image, width: Number(image.width), height: Number(image.height) };
        if (image.id === imageId) {
          const thumb = opener.querySelector('img');
          if (thumb) slide.msrc = thumb.currentSrc || thumb.src;
        }
        return slide;
      })
    };
  }

  function freezePage() {
    const body = document.body;
    const root = document.documentElement;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const properties = ['position', 'top', 'left', 'width', 'overflow'];
    const saved = properties.map((key) => [key, body.style.getPropertyValue(key), body.style.getPropertyPriority(key)]);
    const rootOverflow = [root.style.getPropertyValue('overflow'), root.style.getPropertyPriority('overflow')];
    body.style.setProperty('position', 'fixed');
    body.style.setProperty('top', `${-scrollY}px`);
    body.style.setProperty('left', `${-scrollX}px`);
    body.style.setProperty('width', '100%');
    body.style.setProperty('overflow', 'hidden');
    root.style.setProperty('overflow', 'hidden');
    let restored = false;
    return () => {
      if (restored) return;
      restored = true;
      saved.forEach(([key, value, priority]) => {
        if (value) body.style.setProperty(key, value, priority); else body.style.removeProperty(key);
      });
      if (rootOverflow[0]) root.style.setProperty('overflow', ...rootOverflow);
      else root.style.removeProperty('overflow');
      const behavior = [root.style.getPropertyValue('scroll-behavior'), root.style.getPropertyPriority('scroll-behavior')];
      root.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(scrollX, scrollY);
      if (behavior[0]) root.style.setProperty('scroll-behavior', ...behavior);
      else root.style.removeProperty('scroll-behavior');
    };
  }

  function nativeFallback(request) {
    if (!request.button.isConnected) return;
    const modal = document.getElementById(request.config.modalId);
    if (typeof modal?.show === 'function') {
      modal.show(request.button);
      return;
    }
    // Replay once without interception for themes whose native opener differs.
    bypass.add(request.button);
    try { request.button.click(); } finally { bypass.delete(request.button); }
  }

  // PhotoSwipe's background click action is for the mouse only. A touch
  // single-tap uses tapAction; keep image taps inert and never hide the UI.
  function backgroundTap(_point, event) {
    const target = event?.target;
    if (!(target instanceof Element)) return;
    if (target.closest('.pswp__img, .pswp__button, a, input, select, textarea')) return;
    if (!this.mainScroll.isShifted()) this.close();
  }

  // PhotoSwipe handles gestures inside .pswp__container. The clipped header
  // and footer gutters lie outside that container's hit area. Make a plain
  // tap/click there close as well, without treating any swipe as a click.
  function enableShellDismissal(pswp) {
    const root = pswp.element;
    const controller = new AbortController();
    const signal = controller.signal;
    const pointers = new Set();
    let down = null;
    const shellTarget = (target) => target instanceof Element && root.contains(target) &&
      !target.closest('.pswp__button, a, input, select, textarea') &&
      (!target.closest('.pswp__container') || target === pswp.container);

    root.addEventListener('pointerdown', (event) => {
      pointers.add(event.pointerId);
      if (pointers.size !== 1 || event.button > 0 || !pswp.opener.isOpen ||
          pswp.isDestroying || !shellTarget(event.target)) {
        down = null;
        return;
      }
      down = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }, { capture: true, passive: true, signal });

    window.addEventListener('pointermove', (event) => {
      if (down && event.pointerId === down.id &&
          Math.hypot(event.clientX - down.x, event.clientY - down.y) > 10) down = null;
    }, { passive: true, signal });

    window.addEventListener('pointerup', (event) => {
      const start = down;
      pointers.delete(event.pointerId);
      down = null;
      if (!start || start.id !== event.pointerId || pointers.size ||
          Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) return;
      // Touch pointer events can retain the pointerdown target; hit-test release.
      const hit = document.elementFromPoint(event.clientX, event.clientY);
      if (shellTarget(hit)) requestAnimationFrame(() => {
        if (pswp.opener.isOpen && !pswp.isDestroying) pswp.close();
      });
    }, { passive: true, signal });

    window.addEventListener('pointercancel', (event) => {
      pointers.delete(event.pointerId);
      down = null;
    }, { passive: true, signal });
    return () => controller.abort();
  }

  // Explicit paths avoid cross-SVG <use> references and theme sprite conflicts.
  const icon = (paths) => '<svg class="pswp__icn" aria-hidden="true" ' +
    'viewBox="0 0 32 32" width="32" height="32" fill="none" ' +
    'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
    'stroke-linejoin="round">' + paths + '</svg>';

  function createViewer(PhotoSwipe, request, data) {
    const { config, button, opener } = request;
    const labels = text[config.lang?.slice(0, 2)] || text.en;
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Cached pixel values: beforeResize measures the actual toolbar heights,
    // including iPhone safe areas. PhotoSwipe calls paddingFn very frequently.
    const slidePadding = { top: 72, bottom: 72, left: 8, right: 8 };
    const pswp = new PhotoSwipe({
      dataSource: data.slides,
      index: data.index,
      mainClass: 'lkg-photoswipe',
      arrowPrev: true,
      arrowNext: true,
      close: true,
      zoom: true,
      arrowPrevSVG: icon('<path d="M20 7 11 16l9 9"/>'),
      arrowNextSVG: icon('<path d="m12 7 9 9-9 9"/>'),
      closeSVG: icon('<path d="m8 8 16 16M24 8 8 24"/>'),
      zoomSVG: icon('<circle cx="13" cy="13" r="8"/>' +
        '<path d="m19 19 7 7M9 13h8"/><path class="pswp__zoom-icn-bar-v" d="M13 9v8"/>'),
      bgOpacity: 0.92,
      loop: false,
      initialZoomLevel: 'fit',
      allowPanToNext: true,
      pinchToClose: false,
      closeOnVerticalDrag: true,
      imageClickAction: 'zoom',
      bgClickAction: 'close',
      tapAction: backgroundTap,
      doubleTapAction: 'zoom',
      clickToCloseNonZoomable: false,
      showHideAnimationType: 'fade',
      showAnimationDuration: reduceMotion ? 0 : 180,
      hideAnimationDuration: reduceMotion ? 0 : 180,
      zoomAnimationDuration: reduceMotion ? 0 : 200,
      paddingFn: () => slidePadding,
      preload: [1, 1],
      preloaderDelay: 250,
      trapFocus: true,
      returnFocus: false,
      escKey: true,
      arrowKeys: true,
      closeTitle: labels.close,
      zoomTitle: labels.zoom,
      arrowPrevTitle: labels.previous,
      arrowNextTitle: labels.next,
      errorMsg: labels.error,
      indexIndicatorSep: ' / '
    });

    const restorePage = freezePage();
    const previouslyInert = [];
    let disposed = false;
    let removeShellDismissal = () => {};
    const restore = () => {
      if (disposed) return;
      disposed = true;
      removeShellDismissal();
      previouslyInert.forEach(([element, wasInert]) => {
        if (!wasInert) element.removeAttribute('inert');
      });
      restorePage();
      if (active?.pswp === pswp) active = null;
      requestAnimationFrame(() => {
        if (button.isConnected) button.focus({ preventScroll: true });
      });
    };

    // Reserve real space for the controls rather than positioning arrows
    // over the photograph. Run before PhotoSwipe calculates each slide size.
    pswp.on('beforeResize', () => {
      const element = pswp.element;
      if (!element || !pswp.scrollWrap || !pswp.topBar) return;
      const header = getComputedStyle(pswp.topBar);
      const footer = getComputedStyle(pswp.scrollWrap, '::after');
      const top = parseFloat(header.height) || 64;
      const bottom = parseFloat(footer.height) || 64;
      slidePadding.top = top + 8;
      slidePadding.bottom = bottom + 8;
      slidePadding.left = (parseFloat(header.paddingLeft) || 0) + 8;
      slidePadding.right = (parseFloat(header.paddingRight) || 0) + 8;
      // Clip only the image holders, NOT the horizontally translated container.
      // This keeps a zoomed/panned image out of the control strips too.
      element.style.setProperty('--lkg-clip-top', `${top}px`);
      element.style.setProperty('--lkg-clip-bottom', `${bottom}px`);
    });

    pswp.on('afterInit', () => {
      const element = pswp.element;
      element.dataset.lkgVersion = '1.0.2';
      element.setAttribute('aria-modal', 'true');
      removeShellDismissal = enableShellDismissal(pswp);
      if (getComputedStyle(element).getPropertyValue('--lkg-css-version').trim() !== '102') {
        console.warn('[lokator gallery] Expected complete v1.0.2 gallery CSS. Check the saved asset and reload.');
      }
      element.setAttribute('aria-label', `${labels.gallery}: ${config.productTitle || ''}`);
      const style = getComputedStyle(opener);
      ['--font-body-family', '--font-body-style'].forEach((key) => {
        const value = style.getPropertyValue(key).trim();
        if (value) element.style.setProperty(key, value);
      });
      Array.from(document.body.children).forEach((child) => {
        if (child === element || /^(SCRIPT|STYLE|LINK)$/.test(child.tagName)) return;
        previouslyInert.push([child, child.hasAttribute('inert')]);
        child.setAttribute('inert', '');
      });
    });
    pswp.on('destroy', restore);
    active = { pswp, config, button };
    try {
      if (pswp.init() === false) throw new Error('PhotoSwipe initialization refused.');
    } catch (error) {
      // Unusual startup errors must not leave the store frozen behind an empty dialog.
      try { pswp.events?.removeAll(); pswp.animations?.stopAll(); pswp.element?.remove(); } catch (_) {}
      restore();
      throw error;
    }
    return pswp;
  }

  async function open(request) {
    const { button, opener } = request;
    opening = true;
    const oldBusy = button.getAttribute('aria-busy');
    button.setAttribute('aria-busy', 'true');
    opener.classList.add('lkg--loading');
    try {
      const PhotoSwipe = await dependencies(request.config);
      if (!button.isConnected) return;
      if (!isMobile(request.config)) { nativeFallback(request); return; }
      // Read current markup again: a variant could change while dependencies load.
      const data = slidesFor(request);
      if (!data) { nativeFallback(request); return; }
      createViewer(PhotoSwipe, request, data);
    } catch (error) {
      console.warn('[lokator gallery] Swipe viewer unavailable; opening native viewer.', error);
      nativeFallback(request);
    } finally {
      opening = false;
      opener.classList.remove('lkg--loading');
      if (oldBusy === null) button.removeAttribute('aria-busy');
      else button.setAttribute('aria-busy', oldBusy);
    }
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button > 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    const request = requestFromEvent(event);
    if (!request) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!opening && !active) void open(request);
  }, true);

  // Start loading when the user touches/focuses an image, without opening anything.
  const warm = (event) => {
    const request = requestFromEvent(event);
    if (request) void dependencies(request.config).catch(() => {});
  };
  document.addEventListener('pointerdown', warm, { capture: true, passive: true });
  document.addEventListener('focusin', warm, true);

  function closeActive() {
    if (!active) return;
    const { pswp } = active;
    if (pswp.opener?.isOpen) pswp.close();
    else pswp.on('openingAnimationEnd', () => pswp.close());
  }
  window.addEventListener('resize', () => {
    if (active && !isMobile(active.config)) closeActive();
  }, { passive: true });
  document.addEventListener('shopify:section:unload', (event) => {
    if (active && (event.detail?.sectionId === active.config.sectionId || event.target.contains(active.button))) closeActive();
  });
})();

/* END lokator-swipe-gallery.js v1.0.2 */
